import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { levelService, Level, PageResponse, LevelSearchRequest, LevelSearchFilters } from '../../services/levelService';
import { useRights } from '../../hooks/useRights';
import { useHasAnyActionRight, levelActions } from '../../utils/actionRightsUtils';
import { useModal } from '../../context/ModalContext';
import { Pencil, Trash2 } from 'lucide-react';
import '../../styles/index.css';
import TableActions from '../../components/TableActions';

const Levels: React.FC = () => {
  const { t } = useTranslation();
  const { hasRight } = useRights();
  const { showConfirm, showAlert } = useModal();
  const hasAnyActionRight = useHasAnyActionRight(levelActions());
  const [levels, setLevels] = useState<Level[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [totalElements, setTotalElements] = useState(0);
  const [isDeepSearch, setIsDeepSearch] = useState(false);
  const observerTarget = useRef<HTMLDivElement>(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [filters, setFilters] = useState<LevelSearchFilters>({
    name: '',
    description: '',
  });
  const [editingLevel, setEditingLevel] = useState<Level | null>(null);
  const [editForm, setEditForm] = useState({
    name: '',
    description: '',
  });
  const [isCreating, setIsCreating] = useState(false);
  const [createForm, setCreateForm] = useState({
    name: '',
    description: '',
  });

  useEffect(() => {
    loadLevels(0, true);
  }, []);

  const loadLevels = async (page: number = 0, reset: boolean = true) => {
    try {
      if (reset) {
        setLoading(true);
        setCurrentPage(0);
        setIsDeepSearch(false);
      } else {
        setLoadingMore(true);
      }

      const searchRequest: LevelSearchRequest = {
        page,
        size: 100,
        filters: isDeepSearch ? filters : undefined,
      };

      const response: PageResponse<Level> = await levelService.searchLevels(searchRequest);

      if (reset) {
        setLevels(response.content || []);
      } else {
        setLevels(prev => [...(prev || []), ...(response.content || [])]);
      }

      setCurrentPage(response.page);
      setHasMore(response.hasNext);
      setTotalElements(response.totalElements);
      setError('');
    } catch (e: any) {
      setError(e.response?.data?.message || 'Failed to load levels');
      console.error('Load levels error:', e);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const loadMoreLevels = useCallback(() => {
    if (!loadingMore && hasMore && !loading) {
      setLoadingMore(true);
      const searchFilters: LevelSearchFilters = {
        name: filters.name || undefined,
        description: filters.description || undefined,
      };

      const searchRequest: LevelSearchRequest = {
        page: currentPage + 1,
        size: 100,
        filters: isDeepSearch ? searchFilters : undefined,
      };

      levelService.searchLevels(searchRequest).then(response => {
        setLevels(prev => [...(prev || []), ...(response.content || [])]);
        setCurrentPage(response.page);
        setHasMore(response.hasNext);
        setTotalElements(response.totalElements);
        setLoadingMore(false);
      }).catch(e => {
        setError(e.response?.data?.message || 'Failed to load more levels');
        setLoadingMore(false);
      });
    }
  }, [currentPage, hasMore, loadingMore, loading, isDeepSearch, filters]);

  const handleDeepSearch = async () => {
    try {
      setLoading(true);
      setIsDeepSearch(true);
      setCurrentPage(0);

      const searchFilters: LevelSearchFilters = {
        name: filters.name || undefined,
        description: filters.description || undefined,
      };

      const searchRequest: LevelSearchRequest = {
        page: 0,
        size: 100,
        filters: searchFilters,
      };

      const response: PageResponse<Level> = await levelService.searchLevels(searchRequest);
      setLevels(response.content || []);
      setCurrentPage(response.page);
      setHasMore(response.hasNext);
      setTotalElements(response.totalElements);
      setError('');
    } catch (e: any) {
      setError(e.response?.data?.message || 'Failed to search levels');
      console.error('Deep search error:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const currentTarget = observerTarget.current;
    if (!currentTarget) return;

    const handleScroll = () => {
      const container = currentTarget;
      const scrollTop = container.scrollTop;
      const scrollHeight = container.scrollHeight;
      const clientHeight = container.clientHeight;

      if (scrollHeight - scrollTop - clientHeight < 100 && hasMore && !loadingMore && !loading) {
        loadMoreLevels();
      }
    };

    currentTarget.addEventListener('scroll', handleScroll);

    return () => {
      currentTarget.removeEventListener('scroll', handleScroll);
    };
  }, [loadMoreLevels, hasMore, loadingMore, loading]);

  const filteredLevels = isDeepSearch ? (levels || []) : (levels || []).filter((level) => {
    if (filters.name && !level.name?.toLowerCase().includes(filters.name.toLowerCase())) {
      return false;
    }
    if (filters.description && !level.description?.toLowerCase().includes(filters.description.toLowerCase())) {
      return false;
    }
    return true;
  });

  const handleFilterChange = (key: keyof LevelSearchFilters, value: string | undefined) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setIsDeepSearch(false);
  };

  const clearFilters = () => {
    setFilters({
      name: '',
      description: '',
    });
    setIsDeepSearch(false);
    loadLevels(0, true);
  };

  const handleEdit = (level: Level) => {
    setEditingLevel(level);
    setEditForm({
      name: level.name || '',
      description: level.description || '',
    });
  };

  const handleCancelEdit = () => {
    setEditingLevel(null);
    setEditForm({
      name: '',
      description: '',
    });
  };

  const handleSaveEdit = async () => {
    if (!editingLevel) return;
    if (!editForm.name.trim()) {
      setError(t('levels.nameRequired'));
      return;
    }

    try {
      await levelService.updateLevel(editingLevel.id!, {
        name: editForm.name,
        description: editForm.description,
      });
      await loadLevels(0, true);
      handleCancelEdit();
      setError('');
    } catch (e: any) {
      setError(e.response?.data?.message || 'Failed to update level');
      console.error('Update level error:', e);
    }
  };

  const handleDelete = (id: string) => {
    showConfirm(t('common.delete'), t('levels.deleteConfirm'), async () => {
      try {
        await levelService.deleteLevel(id);
        await loadLevels(0, true);
        setError('');
      } catch (e: any) {
        const errorMsg = e.response?.data || 'Failed to delete level';
        setError(errorMsg);
        showAlert(t('common.error'), errorMsg);
        console.error('Delete level error:', e);
      }
    });
  };

  const handleStartCreate = () => {
    setIsCreating(true);
    setCreateForm({
      name: '',
      description: '',
    });
  };

  const handleCancelCreate = () => {
    setIsCreating(false);
    setCreateForm({
      name: '',
      description: '',
    });
  };

  const handleSaveCreate = async () => {
    if (!createForm.name.trim()) {
      setError(t('levels.nameRequired'));
      return;
    }

    try {
      await levelService.createLevel({
        name: createForm.name,
        description: createForm.description,
      });
      await loadLevels(0, true);
      handleCancelCreate();
      setError('');
    } catch (e: any) {
      setError(e.response?.data?.message || 'Failed to create level');
      console.error('Create level error:', e);
    }
  };

  if (loading && levels.length === 0) {
    return <div className="bo-container">{t('common.loading')}</div>;
  }

  return (
    <div className="bo-container">
      <div className="bo-header">
        <div className="header-left">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="btn-toggle-sidebar"
            title={sidebarOpen ? t('common.hideFilters') : t('common.showFilters')}
          >
            {sidebarOpen ? '◀' : '▶'}
          </button>
          <h2>{t('levels.title')}</h2>
        </div>
        {hasRight('levels:c') && (
          <button className="btn-create" onClick={handleStartCreate}>
            {t('levels.createNew')}
          </button>
        )}
      </div>

      <div className="bo-content-wrapper">
        {sidebarOpen && (
          <div className="bo-sidebar">
            <div className="sidebar-header">
              <h3>{t('levels.filters')}</h3>
              <div className="sidebar-header-actions">
                <button onClick={handleDeepSearch} className="btn-deep-search">
                  {t('levels.deepSearch')}
                </button>
                <button onClick={clearFilters} className="btn-clear-filters">
                  {t('common.clear')}
                </button>
              </div>
            </div>
            {isDeepSearch && (
              <div className="deep-search-indicator">
                <small>{t('levels.deepSearchActive')} ({totalElements} {t('levels.results')})</small>
              </div>
            )}
            <div className="sidebar-body">
              <div className="filter-group">
                <label>{t('levels.name')}</label>
                <input
                  type="text"
                  value={filters.name || ''}
                  onChange={(e) => handleFilterChange('name', e.target.value)}
                  placeholder={t('levels.filterByName')}
                />
              </div>
              <div className="filter-group">
                <label>{t('levels.description')}</label>
                <input
                  type="text"
                  value={filters.description || ''}
                  onChange={(e) => handleFilterChange('description', e.target.value)}
                  placeholder={t('levels.filterByDescription')}
                />
              </div>
            </div>
          </div>
        )}

        <div className={`bo-main-content ${!sidebarOpen ? 'full-width' : ''}`}>
          {error && <div className="error-message">{error}</div>}

          {isCreating && (
            <div className="modal-overlay" onClick={handleCancelCreate}>
              <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <h3>{t('levels.createNew')}</h3>
                <div className="form-group">
                  <div className="form-field">
                    <label>{t('levels.name')} *</label>
                    <input
                      type="text"
                      value={createForm.name}
                      onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                      placeholder={t('levels.namePlaceholder')}
                    />
                  </div>
                  <div className="form-field">
                    <label>{t('levels.description')}</label>
                    <textarea
                      value={createForm.description}
                      onChange={(e) => setCreateForm({ ...createForm, description: e.target.value })}
                      placeholder={t('levels.descriptionPlaceholder')}
                      rows={3}
                    />
                  </div>
                </div>
                <div className="modal-actions">
                  <button className="btn-cancel" onClick={handleCancelCreate}>
                    {t('common.cancel')}
                  </button>
                  <button className="btn-save" onClick={handleSaveCreate}>
                    {t('common.save')}
                  </button>
                </div>
              </div>
            </div>
          )}

          {editingLevel && (
            <div className="modal-overlay" onClick={handleCancelEdit}>
              <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <h3>{t('levels.edit')}</h3>
                <div className="form-group">
                  <div className="form-field">
                    <label>{t('levels.name')} *</label>
                    <input
                      type="text"
                      value={editForm.name}
                      onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                    />
                  </div>
                  <div className="form-field">
                    <label>{t('levels.description')}</label>
                    <textarea
                      value={editForm.description}
                      onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                      rows={3}
                    />
                  </div>
                </div>
                <div className="modal-actions">
                  <button className="btn-cancel" onClick={handleCancelEdit}>
                    {t('common.cancel')}
                  </button>
                  <button className="btn-save" onClick={handleSaveEdit}>
                    {t('common.save')}
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="bo-table-container">
            {filteredLevels.length === 0 && !loading ? (
              <div className="no-data-message">
                <p>{t('levels.noLevelsFound')}</p>
              </div>
            ) : (
              <>
                <div className="bo-table-wrapper" ref={observerTarget}>
                  <table className="bo-table">
                    <thead>
                      <tr>
                        <th>{t('levels.name')}</th>
                        <th>{t('levels.description')}</th>
                        {hasAnyActionRight && <th>{t('common.actions')}</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {filteredLevels.map((level) => (
                        <tr key={level.id}>
                          <td>{level.name}</td>
                          <td>{level.description || '-'}</td>
                          {hasAnyActionRight && (
                            <td>
                              <TableActions actions={[
                                ...(hasRight('levels:u') ? [{
                                  label: t('common.edit'),
                                  onClick: () => handleEdit(level),
                                  className: 'btn-edit',
                                  icon: <Pencil size={16} />,
                                  hideLabelDesktop: true
                                }] : []),
                                ...(hasRight('levels:d') ? [{
                                  label: t('common.delete'),
                                  onClick: () => handleDelete(level.id!),
                                  className: 'btn-delete',
                                  icon: <Trash2 size={16} />,
                                  hideLabelDesktop: true,
                                  variant: 'danger' as const
                                }] : [])
                              ]} />
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="scroll-trigger">
                    {loadingMore && (
                      <div className="loading-more">
                        <small>{t('common.loading')}...</small>
                      </div>
                    )}
                    {!hasMore && levels.length > 0 && (
                      <div className="no-more-data">
                        <small>{t('levels.allLoaded')}</small>
                      </div>
                    )}
                  </div>
                </div>
                {!isDeepSearch && (
                  <div className="table-info">
                    <small>{t('levels.showingLoaded')} ({levels.length} {t('levels.loaded')})</small>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Levels;
